// Dependencias
import { Chart, registerables } from 'chart.js';
import Swal from 'sweetalert2';
import { AxiosError } from 'axios';

// Configuraciones
import { alertConfigurations, toastConfiguration } from '../config/sweetAlert.config';
import { ErrorMessagesToast } from '../config/Messages.config';

// Utils
import { drawApiToggle } from '../utils/drawTable';
import { loadLoader } from '../utils/loader';
import {
  escapeHtml,
  formatDateTime,
  drawApiKeyStatus,
  drawApiKeyEnvironment,
  drawApiKeyScopes,
  drawApiKeyLastUse,
  drawApiKeyRevoked,
  drawApiKeyActions,
  revokeApiKeyFlow,
  onDomReady
} from '../utils/publicApi';

// Services
import * as publicApiService from '../service/publicApi.service';
// Se reutiliza el endpoint existente PATCH /users/:id/api (no se duplica la lógica del permiso)
import { toggleUserApi } from '../service/client.service';

// Configuración de carga de componentes de gráficas
Chart.register(...registerables);

// Instancias
const toast = Swal.mixin(toastConfiguration);
const DAYS = 30;

// Variables globales
let requestsChart: Chart<'bar'> | null = null;
let detailMode: 'key' | 'user' = 'key';
let detailId = 0;

/**
 * Pinta la información de un usuario (incluye el switch del permiso users.api)
 */
function drawUserInfo(user: {
  id: number;
  name: string | null;
  last_name: string | null;
  email: string | null;
  api: number | null;
  blocked: number | null;
  plan?: string | null;
}, { showLink = false } = {}) {
  const fullName = [user.name, user.last_name].filter(Boolean).join(' ') || 'Sin nombre';
  const link = showLink
    ? `<a class="btn btn-outline-light btn-sm mt-2" href="/public-api/users/${Number(user.id)}"><i class="bi bi-person-lines-fill"></i> Ver uso del usuario</a>`
    : '';

  return `
    <div class="col-md-6">
      <p class="h5 fw-bold"><i class="bi bi-person me-2"></i>Usuario</p>
      <dl class="row mb-0">
        <dt class="col-4 text-white-50">Nombre</dt><dd class="col-8">${escapeHtml(fullName)}</dd>
        <dt class="col-4 text-white-50">Correo</dt><dd class="col-8">${escapeHtml(user.email || '')}</dd>
        <dt class="col-4 text-white-50">ID</dt><dd class="col-8">${Number(user.id)}</dd>
        ${user.plan !== undefined ? `<dt class="col-4 text-white-50">Plan</dt><dd class="col-8">${escapeHtml(user.plan || 'N/A')}</dd>` : ''}
        <dt class="col-4 text-white-50">Bloqueado</dt>
        <dd class="col-8">${user.blocked ? '<span class="badge bg-danger">Sí</span>' : '<span class="badge bg-success">No</span>'}</dd>
        <dt class="col-4 text-white-50">Permiso API (users.api)</dt>
        <dd class="col-8">
          <div class="d-flex align-items-center gap-2">
            ${drawApiToggle(Number(user.id), Boolean(user.api))}
            <span class="small text-white-50">Con api=0 el backend rechaza todas sus requests a /api/v1.</span>
          </div>
        </dd>
      </dl>
      ${link}
    </div>
  `;
}

/**
 * Pinta la información de una key
 */
function drawKeyInfo(key: publicApiService.ApiKey) {
  return `
    <div class="col-md-6">
      <p class="h5 fw-bold"><i class="bi bi-key me-2"></i>API key</p>
      <dl class="row mb-0">
        <dt class="col-4 text-white-50">Nombre</dt><dd class="col-8">${escapeHtml(key.name)}</dd>
        <dt class="col-4 text-white-50">Prefijo</dt><dd class="col-8"><code>${escapeHtml(key.key_prefix)}</code></dd>
        <dt class="col-4 text-white-50">Ambiente</dt><dd class="col-8">${drawApiKeyEnvironment(key.environment)}</dd>
        <dt class="col-4 text-white-50">Scopes</dt><dd class="col-8">${drawApiKeyScopes(key.scopes)}</dd>
        <dt class="col-4 text-white-50">Estado</dt><dd class="col-8">${drawApiKeyStatus(key.status)}</dd>
        <dt class="col-4 text-white-50">IPs permitidas</dt><dd class="col-8">${key.allowed_ips ? escapeHtml(key.allowed_ips) : '<span class="text-white-50">Todas</span>'}</dd>
        <dt class="col-4 text-white-50">Creada</dt><dd class="col-8">${formatDateTime(key.created_at)} <span class="small text-white-50">${escapeHtml(key.created_by || '')}</span></dd>
        <dt class="col-4 text-white-50">Último uso</dt><dd class="col-8">${drawApiKeyLastUse(key)}</dd>
        <dt class="col-4 text-white-50">Expira</dt><dd class="col-8">${formatDateTime(key.expires_at, '<span class="text-white-50">Nunca</span>')}</dd>
        <dt class="col-4 text-white-50">Revocación</dt><dd class="col-8">${drawApiKeyRevoked(key)}</dd>
      </dl>
      <div class="mt-2">${drawApiKeyActions(key, { showDetail: false })}</div>
    </div>
  `;
}

/**
 * Gráfica de requests por día separando 2xx / 4xx / 5xx (y otros si existen)
 */
function drawRequestsChart(usage: publicApiService.ApiUsage) {
  const canvas = document.querySelector<HTMLCanvasElement>('#api-requests-chart');
  if (!canvas) return;

  const labels = usage.daily.map(d => d.day);
  const others = usage.daily.map(d => Math.max(d.total - d.s2xx - d.s4xx - d.s5xx, 0));
  const datasets = [
    { label: '2xx', data: usage.daily.map(d => d.s2xx), backgroundColor: 'rgba(25, 135, 84, 0.85)' },
    { label: '4xx', data: usage.daily.map(d => d.s4xx), backgroundColor: 'rgba(255, 193, 7, 0.85)' },
    { label: '5xx', data: usage.daily.map(d => d.s5xx), backgroundColor: 'rgba(220, 53, 69, 0.85)' }
  ];
  if (others.some(Boolean)) datasets.push({ label: 'Otros', data: others, backgroundColor: 'rgba(108, 117, 125, 0.85)' });

  if (requestsChart) requestsChart.destroy();
  requestsChart = new Chart(canvas, {
    type: 'bar',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: '#ffffff', font: { size: 12, weight: 'bold' } } },
        tooltip: { mode: 'index', intersect: false }
      },
      scales: {
        x: { stacked: true, ticks: { color: '#e2e8f0' }, grid: { color: 'rgba(255, 255, 255, 0.1)' } },
        y: { stacked: true, beginAtZero: true, ticks: { color: '#e2e8f0', precision: 0 }, grid: { color: 'rgba(255, 255, 255, 0.1)' } }
      }
    }
  });
}

/**
 * Badge para un status HTTP
 */
function drawStatusCode(status: number) {
  const color = status >= 500 ? 'bg-danger' : status >= 400 ? 'bg-warning text-dark' : status >= 200 && status < 300 ? 'bg-success' : 'bg-secondary';
  return `<span class="badge ${color}">${Number(status)}</span>`;
}

/**
 * Pinta top rutas y últimas requests
 */
function drawUsageTables(usage: publicApiService.ApiUsage) {
  const topRoutesBody = document.querySelector<HTMLTableSectionElement>('#detail-top-routes');
  const recentBody = document.querySelector<HTMLTableSectionElement>('#detail-recent-requests');
  const daysElement = document.querySelector<HTMLElement>('#detail-days');
  if (daysElement) daysElement.textContent = String(usage.days);

  if (topRoutesBody) {
    topRoutesBody.innerHTML = usage.topRoutes.length
      ? usage.topRoutes.map(route => `
        <tr>
          <td><span class="badge bg-secondary">${escapeHtml(route.method)}</span></td>
          <td><code>${escapeHtml(route.route)}</code></td>
          <td class="text-end">${Number(route.total)}</td>
          <td class="text-end">${Number(route.errors)}</td>
          <td class="text-end">${Number(route.avgDurationMs)}</td>
        </tr>`).join('')
      : '<tr><td colspan="5" class="text-white-50">Sin requests en el periodo</td></tr>';
  }

  if (recentBody) {
    recentBody.innerHTML = usage.recent.length
      ? usage.recent.map(log => `
        <tr>
          <td class="text-nowrap">${formatDateTime(log.created_at)}</td>
          <td><a class="link-light" href="/public-api/keys/${Number(log.api_key_id)}"><code>${escapeHtml(log.key_prefix || `#${log.api_key_id}`)}</code></a></td>
          <td><span class="badge bg-secondary">${escapeHtml(log.method)}</span></td>
          <td><code>${escapeHtml(log.route)}</code></td>
          <td>${log.document_id ? Number(log.document_id) : '<span class="text-white-50">-</span>'}</td>
          <td>${drawStatusCode(log.status_code)}</td>
          <td class="text-end">${Number(log.duration_ms)}</td>
          <td>${escapeHtml(log.ip)}</td>
          <td><code class="small">${escapeHtml(log.request_id)}</code></td>
        </tr>`).join('')
      : '<tr><td colspan="9" class="text-white-50">Sin requests en el periodo</td></tr>';
  }
}

/**
 * Pinta la tabla de keys de un usuario
 */
function drawUserKeys(keys: publicApiService.ApiKey[]) {
  const body = document.querySelector<HTMLTableSectionElement>('#detail-user-keys');
  if (!body) return;

  body.innerHTML = keys.length
    ? keys.map(key => `
      <tr>
        <td>${Number(key.id)}</td>
        <td>${escapeHtml(key.name)}</td>
        <td><code>${escapeHtml(key.key_prefix)}</code></td>
        <td>${drawApiKeyEnvironment(key.environment)}</td>
        <td>${drawApiKeyScopes(key.scopes)}</td>
        <td>${drawApiKeyStatus(key.status)}</td>
        <td class="text-nowrap">${formatDateTime(key.created_at)}</td>
        <td class="text-nowrap">${drawApiKeyLastUse(key)}</td>
        <td class="text-nowrap">${formatDateTime(key.expires_at, '<span class="text-white-50">Nunca</span>')}</td>
        <td>${drawApiKeyRevoked(key)}</td>
        <td>${drawApiKeyActions(key)}</td>
      </tr>`).join('')
    : '<tr><td colspan="11" class="text-white-50">El usuario no tiene API keys</td></tr>';
}

/**
 * Muestra un error general en la vista
 */
function showError(message: string) {
  const errorElement = document.querySelector<HTMLElement>('#detail-error');
  const infoElement = document.querySelector<HTMLElement>('#detail-info');
  if (errorElement) {
    errorElement.textContent = message;
    errorElement.classList.remove('d-none');
  }
  if (infoElement) infoElement.innerHTML = '';
}

/**
 * Carga (o recarga) toda la información del detalle
 */
async function loadDetail() {
  const infoElement = document.querySelector<HTMLElement>('#detail-info');
  if (!infoElement) return;

  try {
    if (detailMode === 'user') {
      const { user, keys, usage } = await publicApiService.fetchApiUserDetail(detailId, DAYS);
      infoElement.innerHTML = `<div class="row">${drawUserInfo(user)}</div>`;
      drawUserKeys(keys);
      drawRequestsChart(usage);
      drawUsageTables(usage);
      return;
    }

    const { key, usage } = await publicApiService.fetchApiKeyDetail(detailId, DAYS);
    infoElement.innerHTML = `
      <div class="row g-4">
        ${drawKeyInfo(key)}
        ${drawUserInfo({
          id: key.user_id,
          name: key.user_name,
          last_name: key.user_last_name,
          email: key.user_email,
          api: key.user_api,
          blocked: key.user_blocked
        }, { showLink: true })}
      </div>`;
    drawRequestsChart(usage);
    drawUsageTables(usage);
  } catch (error) {
    console.error('Error al obtener el detalle de la API pública:', error);
    const notFound = error instanceof AxiosError && error.response?.status === 404;
    showError(notFound
      ? (detailMode === 'user' ? 'Usuario no encontrado' : 'API key no encontrada')
      : ErrorMessagesToast.ERROR);
  }
}

/**
 * Cambio del permiso users.api reutilizando el endpoint existente PATCH /users/:id/api
 */
async function handleApiToggleChange(target: HTMLInputElement) {
  const userId = parseInt(target.dataset.userId || '0', 10);
  const enabled = target.checked;
  if (!userId) return;

  const confirmation = await Swal.fire({
    ...alertConfigurations,
    icon: 'question',
    title: enabled ? '¿Habilitar el permiso de API?' : '¿Deshabilitar el permiso de API?',
    text: enabled
      ? 'El usuario podrá volver a usar sus API keys activas.'
      : 'El backend rechazará todas las requests de este usuario a /api/v1, aunque sus keys sigan activas.'
  });
  if (!confirmation.isConfirmed) {
    target.checked = !enabled;
    return;
  }

  try {
    loadLoader({});
    target.disabled = true;
    await toggleUserApi(userId, enabled);
    toast.fire({ icon: 'success', title: enabled ? 'API activada correctamente' : 'API desactivada correctamente' });
  } catch (error) {
    target.checked = !enabled;
    const message = error instanceof AxiosError
      ? (error.response?.data?.message as string) || 'Error al actualizar el estado de la API'
      : ErrorMessagesToast.ERROR;
    toast.fire({ icon: 'error', title: message });
  } finally {
    target.disabled = false;
    loadLoader({ start: false });
  }
}

export function main() {
  onDomReady(() => {
    const container = document.querySelector<HTMLElement>('#public-api-detail');
    if (!container) return;

    detailMode = container.dataset.mode === 'user' ? 'user' : 'key';
    detailId = parseInt(container.dataset.id || '0', 10);
    if (!detailId) {
      showError('Identificador no válido');
      return;
    }

    loadDetail();

    // Toggle del permiso users.api (delegación de eventos)
    container.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.classList.contains('api-toggle')) handleApiToggleChange(target);
    });

    // Revocar key (delegación de eventos)
    container.addEventListener('click', async(e) => {
      const button = (e.target as HTMLElement).closest<HTMLButtonElement>('.btn-revoke-api-key');
      if (!button) return;

      const revoked = await revokeApiKeyFlow({
        id: Number(button.dataset.keyId),
        name: button.dataset.keyName || '',
        prefix: button.dataset.keyPrefix || ''
      });
      if (revoked) loadDetail();
    });
  });
}
